import { useState } from "react";
import { useApp } from "@/context/AppContext";
import { Plus, Trash, User, CheckCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const COLORS = [
  { id: "amber",   label: "Amber",   ring: "ring-amber-500",   bg: "bg-amber-500",   text: "text-amber-500",   dim: "bg-amber-500/10" },
  { id: "emerald", label: "Emerald", ring: "ring-emerald-500", bg: "bg-emerald-500", text: "text-emerald-500", dim: "bg-emerald-500/10" },
  { id: "sky",     label: "Sky",     ring: "ring-sky-500",     bg: "bg-sky-400",     text: "text-sky-400",     dim: "bg-sky-500/10" },
  { id: "purple",  label: "Purple",  ring: "ring-purple-500",  bg: "bg-purple-500",  text: "text-purple-500",  dim: "bg-purple-500/10" },
];

const colorMap = Object.fromEntries(COLORS.map(c => [c.id, c]));

function PersonaCard({ persona, isActive, onSelect, onDelete }) {
  const c = colorMap[persona.color] || colorMap.amber;
  return (
    <div
      data-testid={`persona-card-${persona.id}`}
      onClick={() => onSelect(persona.id)}
      className={`relative group cursor-pointer flex flex-col items-center gap-3 p-5 rounded-xl border transition-all duration-200 hover:scale-105 hover:shadow-lg
        ${isActive
          ? `border-2 ${c.ring} bg-white/[0.04] shadow-lg`
          : 'border border-white/10 bg-white/[0.02] hover:border-white/20'
        }`}
      style={{ width: 148, minHeight: 180 }}
    >
      {/* Active indicator */}
      {isActive && (
        <div className={`absolute top-2 right-2 ${c.text}`}>
          <CheckCircle size={16} weight="fill" />
        </div>
      )}

      {/* Avatar circle */}
      <div className={`w-20 h-20 rounded-full ${c.dim} border-2 ${isActive ? c.ring : 'border-white/10'} flex items-center justify-center transition-all duration-200 group-hover:${c.ring}`}>
        <span className={`font-['Outfit'] text-3xl font-bold ${c.text}`}>
          {persona.name.charAt(0).toUpperCase()}
        </span>
      </div>

      {/* Name & role */}
      <div className="text-center">
        <div className="text-white text-sm font-semibold font-['Outfit'] truncate max-w-[120px]">
          {persona.name}
        </div>
        {persona.role_title && (
          <div className="text-zinc-500 text-[11px] mt-0.5 truncate max-w-[120px]">{persona.role_title}</div>
        )}
        {persona.has_resume && (
          <div className={`text-[10px] mt-1.5 font-medium ${c.text} flex items-center justify-center gap-1`}>
            <CheckCircle size={10} weight="fill" /> Resume loaded
          </div>
        )}
        {!persona.has_resume && (
          <div className="text-[10px] mt-1.5 text-zinc-700">No resume yet</div>
        )}
      </div>

      {/* Delete button */}
      <button
        data-testid={`persona-delete-${persona.id}`}
        onClick={(e) => { e.stopPropagation(); onDelete(persona.id); }}
        className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 text-zinc-600 hover:text-red-400 rounded"
        title="Delete persona"
      >
        <Trash size={13} />
      </button>
    </div>
  );
}

function CreatePersonaCard({ onCreate, disabled }) {
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [roleTitle, setRoleTitle] = useState("");
  const [color, setColor] = useState("amber");

  const handleCreate = async () => {
    if (!name.trim()) { toast.error("Enter a persona name"); return; }
    await onCreate({ name: name.trim(), role_title: roleTitle.trim(), color });
    setName(""); setRoleTitle(""); setColor("amber");
    setShowForm(false);
  };

  if (!showForm) {
    return (
      <button
        data-testid="create-persona-card"
        onClick={() => !disabled && setShowForm(true)}
        disabled={disabled}
        className={`flex flex-col items-center justify-center gap-3 p-5 rounded-xl border border-dashed transition-all duration-200
          ${disabled
            ? 'border-white/5 text-zinc-700 cursor-not-allowed'
            : 'border-white/15 text-zinc-500 hover:border-white/30 hover:text-white hover:bg-white/[0.02] hover:scale-105 cursor-pointer'
          }`}
        style={{ width: 148, minHeight: 180 }}
      >
        <div className="w-20 h-20 rounded-full bg-white/[0.02] border border-dashed border-white/10 flex items-center justify-center">
          <Plus size={28} weight="light" />
        </div>
        <div className="text-center">
          <div className="text-sm font-medium font-['Outfit']">
            {disabled ? "Max 4 personas" : "Add Persona"}
          </div>
          {!disabled && <div className="text-[10px] text-zinc-700 mt-0.5">Create a new search profile</div>}
        </div>
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-4 rounded-xl border border-amber-500/30 bg-amber-500/5" style={{ width: 280 }}>
      <div className="jp-label mb-1">NEW PERSONA</div>

      <input
        autoFocus
        data-testid="persona-name-input"
        placeholder="Name (e.g. Senior Dev)"
        value={name}
        onChange={e => setName(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && handleCreate()}
        className="bg-black/40 border border-white/10 rounded-md h-9 px-3 text-sm text-white placeholder:text-zinc-700 outline-none focus:border-amber-500/40"
      />
      <input
        data-testid="persona-role-input"
        placeholder="Role (e.g. Backend Engineer)"
        value={roleTitle}
        onChange={e => setRoleTitle(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && handleCreate()}
        className="bg-black/40 border border-white/10 rounded-md h-9 px-3 text-sm text-white placeholder:text-zinc-700 outline-none focus:border-amber-500/40"
      />

      {/* Color picker */}
      <div>
        <div className="jp-label mb-2">ACCENT COLOR</div>
        <div className="flex gap-2">
          {COLORS.map(c => (
            <button
              key={c.id}
              onClick={() => setColor(c.id)}
              className={`w-6 h-6 rounded-full ${c.bg} transition-transform ${color === c.id ? 'scale-125 ring-2 ring-white/30' : 'opacity-60 hover:opacity-100'}`}
            />
          ))}
        </div>
      </div>

      <div className="flex gap-2 mt-1">
        <Button
          data-testid="persona-create-confirm-btn"
          onClick={handleCreate}
          size="sm"
          className="flex-1 bg-amber-500 text-black hover:bg-amber-400 rounded-md h-8 text-xs font-medium"
        >
          Create
        </Button>
        <Button
          onClick={() => setShowForm(false)}
          size="sm"
          className="bg-transparent border border-white/10 text-zinc-500 hover:bg-white/5 rounded-md h-8 px-3 text-xs"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

export default function PersonaSelector({ onDismiss }) {
  const { personas, activePersonaId, setActivePersonaId, createPersona, deletePersona, loading } = useApp();

  const handleSelect = (id) => {
    setActivePersonaId(id);
    toast.success(`Switched to ${personas.find(p => p.id === id)?.name}`);
    onDismiss();
  };

  const handleCreate = async (data) => {
    const result = await createPersona(data);
    if (result?.error) {
      toast.error(result.error);
    } else {
      toast.success(`Persona "${result.name}" created`);
      // Auto-select if first persona
      if (personas.length === 0) {
        setActivePersonaId(result.id);
        onDismiss();
      }
    }
  };

  const handleDelete = async (id) => {
    const persona = personas.find(p => p.id === id);
    await deletePersona(id);
    toast.success(`Deleted "${persona?.name}"`);
  };

  const canAddMore = personas.length < 4;

  return (
    <div
      data-testid="persona-selector-overlay"
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/95 backdrop-blur-sm"
      style={{ background: 'radial-gradient(ellipse at 50% 40%, rgba(30,20,10,0.98) 0%, #050505 70%)' }}
    >
      {/* Heading */}
      <div className="mb-10 text-center animate-in">
        <h1 className="font-['Outfit'] text-4xl font-semibold text-white tracking-tight mb-2">
          Who's searching today?
        </h1>
        <p className="text-zinc-500 text-sm">
          Each persona has its own resume, search history, and scoring profile.
          {personas.length > 0 ? " Select one to continue." : " Create your first persona to get started."}
        </p>
      </div>

      {/* Personas grid */}
      <div className="flex flex-wrap items-start justify-center gap-4 animate-in delay-1" data-testid="persona-grid">
        {personas.map(p => (
          <PersonaCard
            key={p.id}
            persona={p}
            isActive={p.id === activePersonaId}
            onSelect={handleSelect}
            onDelete={handleDelete}
          />
        ))}

        {/* Add persona card */}
        <CreatePersonaCard
          onCreate={handleCreate}
          disabled={!canAddMore}
        />
      </div>

      {/* Max personas note */}
      {personas.length >= 4 && (
        <p className="mt-6 text-xs text-zinc-700 animate-in delay-2">
          Maximum 4 personas reached. Delete one to add another.
        </p>
      )}

      {/* Dismiss */}
      {personas.length > 0 && (
        <button
          data-testid="persona-selector-dismiss"
          onClick={onDismiss}
          className="mt-10 text-xs text-zinc-600 hover:text-zinc-400 transition-colors animate-in delay-2 underline underline-offset-2"
        >
          Continue as "{personas.find(p => p.id === activePersonaId)?.name || 'current persona'}"
        </button>
      )}

      {/* Loading overlay */}
      {loading.createPersona && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
          <div className="text-amber-500 text-sm font-mono">Creating persona...</div>
        </div>
      )}
    </div>
  );
}
